// Macro-History-Store — Zustand mit localStorage-Persist. V1-Pendant:
// `loadHistory`/`saveHistory` aus useMacroHistory.js.
//
// Verhalten:
// - `saveDailySnapshot` ist idempotent pro Tag (V1: skippt wenn last.date === today).
// - Cap auf MAX_MACRO_DAYS (30) — älteste fliegen raus.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MAX_MACRO_DAYS, buildMacroSnapshot } from '@/lib/macro-history';
import type { MacroSnapshotEntry } from '@/types/macro-history';
import type { FullData, MarketStats } from '@/types/market';

export const MACRO_HISTORY_STORE_KEY = 'eve_macro_history_v3';

export interface MacroHistoryState {
  entries: MacroSnapshotEntry[];
}

export interface MacroHistoryActions {
  /**
   * Speichert einen Snapshot — V1-Verhalten: wenn für heute schon einer existiert,
   * No-Op. Gibt das gespeicherte Entry zurück oder `null` wenn bereits vorhanden.
   */
  saveDailySnapshot: (fullData: FullData, marketStats: MarketStats, now?: Date) => MacroSnapshotEntry | null;
  /** Hard-set für Tests + D1-Sync. */
  hydrate: (entries: MacroSnapshotEntry[]) => void;
  clearAll: () => void;
}

export type MacroHistoryStore = MacroHistoryState & MacroHistoryActions;

export const useMacroHistoryStore = create<MacroHistoryStore>()(
  persist(
    (set, get) => ({
      entries: [],

      saveDailySnapshot: (fullData, marketStats, now = new Date()) => {
        const today = now.toISOString().slice(0, 10);
        const current = get().entries;
        const last = current[current.length - 1];
        if (last && last.date === today) return null;
        const snapshot = buildMacroSnapshot(fullData, marketStats, now);
        const next = [...current, snapshot];
        set({
          entries: next.length > MAX_MACRO_DAYS ? next.slice(-MAX_MACRO_DAYS) : next,
        });
        return snapshot;
      },

      hydrate: (entries) => {
        set({
          entries:
            entries.length > MAX_MACRO_DAYS ? entries.slice(-MAX_MACRO_DAYS) : entries,
        });
      },

      clearAll: () => set({ entries: [] }),
    }),
    {
      name: MACRO_HISTORY_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries }),
      version: 1,
    },
  ),
);

export const selectMacroEntries = (s: MacroHistoryState): MacroSnapshotEntry[] => s.entries;
